import type { ReactNode } from 'react'

export type ShellWidth = 'read' | 'form' | 'app' | 'report'
export type ShellPad = 'compact' | 'roomy' | 'none'

// `app` and `report` stay narrow below Tailwind's lg breakpoint and widen above
// it, which is what /build and the results surfaces already do. Preserving that
// is the whole point: the wide layouts are laid out for a wide column and the
// narrow ones are not.
const WIDTH_RESPONSIVE: Record<ShellWidth, string> = {
  read: 'max-w-read',
  form: 'max-w-form',
  app: 'max-w-form lg:max-w-app',
  report: 'max-w-form lg:max-w-report',
}

// Only AdminDashboardPage needs this: it is max-w-6xl with no breakpoint
// prefix, so the responsive recipe would cap it at 768px between 768px and
// 1024px of viewport width.
const WIDTH_FIXED: Record<ShellWidth, string> = {
  read: 'max-w-read',
  form: 'max-w-form',
  app: 'max-w-app',
  report: 'max-w-report',
}

// Two presets cover 13 of 17 pages. The other four shapes are one-offs and are
// preserved verbatim by their pages via padY="none" plus className. This
// deliberately does NOT unify today's padding: that would be a visible change.
const PAD_Y: Record<ShellPad, string> = {
  compact: 'py-8 pb-24 md:pb-8',
  roomy: 'py-12 pb-24 md:pb-8',
  none: '',
}

export interface PageShellProps {
  width: ShellWidth
  responsive?: boolean
  padY?: ShellPad
  /** false for a shell nested inside an already-padded wrapper. */
  padX?: boolean
  className?: string
  children: ReactNode
}

export default function PageShell({
  width,
  responsive = true,
  padY = 'compact',
  padX = true,
  className,
  children,
}: PageShellProps) {
  // w-full matters inside a flex parent with items-center, where a block child
  // would otherwise shrink to its content. HomePage's outer div is exactly
  // that, which is why its inner container already carries w-full today.
  const classes = [
    // eslint-disable-next-line i18next/no-literal-string -- CSS classes
    'mx-auto w-full',
    (responsive ? WIDTH_RESPONSIVE : WIDTH_FIXED)[width],
    // eslint-disable-next-line i18next/no-literal-string -- CSS classes
    padX ? 'px-4' : '',
    PAD_Y[padY],
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ')

  return <div className={classes}>{children}</div>
}
